import { Modal, Tabs, type TabsTabProps } from "@mantine/core";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router";
import { useMeteringOrMarketLocation } from "../../hooks/useMeteringOrMarketLocation";
import { useMeteringOrMarketLocationMutation } from "../../hooks/useMeteringOrMarketLocationMutation";
import { useMissingObjectData } from "../../hooks/useMissingObjectData";
import { TabWithHighlightAndError } from "../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import { useToast } from "../../mantine/notifications/useToast";
import type {
  BaseWizardProps,
  NestedSaveProps
} from "../../types/wizard.types";
import type { MeteringOrMarketLocation } from "../../utils/backend-types";
import { ObjectName } from "../../utils/enums";
import { buildFieldNameToLabelMap } from "../../utils/react-hook-form/buildFieldNameToLabelMap";
import { HistoryTab } from "../HistoryTab/HistoryTab";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../StaffViewToggle/useShouldShowStaffView";
import { useWizardStack } from "../WizardStack/WizardStackContext";
import { MeteringLocationForm } from "./MeteringLocationForm/MeteringLocationForm";
import { METERING_LOCATION_FORM_FIELD_DATA } from "./MeteringLocationForm/MeteringLocationForm.constants";

type TabProps = TabsTabProps & { label: string };

const TABS: Array<TabProps> = [
  {
    value: "general",
    label: "Allgemeine Daten"
  },
  {
    value: "historie",
    label: "Historie"
  }
];

type MeteringLocationWizardProps = BaseWizardProps &
  NestedSaveProps<MeteringOrMarketLocation> & {
    siteId: number;
    size?: "lg" | "xl";
    stackId?: string;
  };

function MeteringLocationWizard({
  isOpen,
  siteId,
  size = "xl",
  stackId,
  onClose,
  ...modeProps
}: MeteringLocationWizardProps) {
  const { showToast } = useToast();
  const { depth } = useWizardStack();
  const [searchParams, setSearchParams] = useSearchParams();
  const { shouldShowStaffView } = useShouldShowStaffView();
  const { mutateAsync } = useMeteringOrMarketLocationMutation(
    ObjectName.MeteringLocation,
    modeProps.mode,
    siteId
  );

  const meteringLocationId =
    modeProps.mode === "edit" ? modeProps.objectId : undefined;

  const {
    data: meteringLocation,
    isLoading: isMeteringLocationLoading,
    error: meteringLocationError
  } = useMeteringOrMarketLocation(
    ObjectName.MeteringLocation,
    meteringLocationId
  );

  const {
    isLoading: isMissingDataLoading,
    error: missingDataError,
    fieldsThatCouldBeMissing,
    shouldFieldBeHighlighted
  } = useMissingObjectData(meteringLocationId, siteId);

  const [numErrors, setNumErrors] = useState(0);
  const [highlightedTabs, setHighlightedTabs] = useState<Array<string>>([]);

  async function handleSubmit(formData: MeteringOrMarketLocation) {
    return mutateAsync(formData, {
      onSuccess: (response) => {
        if (modeProps.context === "nested") {
          modeProps.onNestedSave(response.data);
        } else {
          showToast({
            type: "success",
            message:
              modeProps.mode === "create"
                ? "Die Messlokation wurde erstellt."
                : "Die Änderungen wurden gespeichert."
          });
        }
        onClose();
      }
    });
  }

  const handleChangeNumErrors = useCallback((numErrors: number) => {
    setNumErrors(numErrors);
  }, []);

  const handleChangeNumHighlightedFields = useCallback(
    (numHighlightedFields: number) => {
      setHighlightedTabs(numHighlightedFields > 0 ? [TABS[0].value] : []);
    },
    []
  );

  const fieldNameToLabelMap =
    buildFieldNameToLabelMap<MeteringOrMarketLocation>(
      METERING_LOCATION_FORM_FIELD_DATA
    );

  function getActiveTab() {
    if (modeProps.context === "nested") {
      return TABS[0].value;
    }

    const requestedTab = searchParams.get("tab") ?? TABS[0].value;
    const requestedTabIsValid = TABS.some((tab) => tab.value === requestedTab);

    return requestedTabIsValid ? requestedTab : TABS[0].value;
  }

  const activeTab = getActiveTab();

  const action = modeProps.mode === "create" ? "erstellen" : "bearbeiten";
  const title = meteringLocation?.name
    ? `${meteringLocation.name} ${action}`
    : `${ObjectName.MeteringLocation} ${action}`;

  const isLoading =
    modeProps.mode === "edit" &&
    (isMeteringLocationLoading || isMissingDataLoading);
  const error =
    modeProps.mode === "edit"
      ? meteringLocationError || missingDataError
      : null;

  function getFormModeProps() {
    if (modeProps.mode === "create") {
      return { mode: "create" as const };
    }

    if (meteringLocation) {
      return {
        mode: "edit" as const,
        wizardObject: meteringLocation,
        fieldsThatCouldBeMissing,
        shouldFieldBeHighlighted
      };
    }

    return null;
  }

  const formModeProps = getFormModeProps();

  return (
    <Modal
      className="MeteringLocationWizard"
      closeOnEscape={depth === 0}
      opened={isOpen}
      size={size}
      stackId={stackId}
      title={title}
      onClose={onClose}
    >
      <LoadOrError error={error} loading={isLoading}>
        <Tabs
          value={activeTab}
          onChange={(newTab) => {
            if (!newTab) {
              return;
            }

            searchParams.set("tab", newTab);
            setSearchParams(searchParams);
          }}
        >
          {modeProps.mode === "edit" && modeProps.context !== "nested" && (
            <Tabs.List>
              {TABS.map((tab, index) => (
                <TabWithHighlightAndError
                  component="a"
                  highlighted={highlightedTabs.includes(tab.value)}
                  key={tab.value}
                  numErrors={index === 0 ? numErrors : 0}
                  value={tab.value}
                >
                  {tab.label}
                </TabWithHighlightAndError>
              ))}
            </Tabs.List>
          )}
          <Tabs.Panel value={TABS[0].value}>
            {formModeProps && (
              <MeteringLocationForm
                showStaffView={shouldShowStaffView}
                onCancel={onClose}
                onChangeNumErrors={handleChangeNumErrors}
                onChangeNumHighlightedFields={handleChangeNumHighlightedFields}
                onSubmit={handleSubmit}
                {...formModeProps}
              />
            )}
          </Tabs.Panel>
          {modeProps.mode === "edit" && modeProps.context !== "nested" && (
            <Tabs.Panel value={TABS[1].value}>
              <HistoryTab
                entityId={modeProps.objectId}
                entityType={ObjectName.MeteringLocation}
                fieldNameToLabelMap={fieldNameToLabelMap}
              />
            </Tabs.Panel>
          )}
        </Tabs>
      </LoadOrError>
    </Modal>
  );
}

export { MeteringLocationWizard, MeteringLocationWizardProps };
